import SaasBrandMark from './SaasBrandMark';

/**
 * Lightweight first paint for public marketing routes.
 * Avoids flashing BrandedLoadingScreen (looks like a second homepage) before LandingPage mounts.
 */
export default function SaasMarketingBootShell() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#ecfdf5]/50 via-white to-white font-jakarta text-slate-700 antialiased"
      data-testid="saas-marketing-boot-shell"
      aria-busy="true"
      aria-label="Loading ServiceApp"
    >
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-2.5 px-4 sm:h-[4.25rem] sm:px-6">
          <SaasBrandMark src="/serviceapp-logo.png" alt="" size="sm" />
          <span className="text-lg font-bold tracking-tight text-slate-900">
            ServiceApp
            <span className="text-teal-700">.ai</span>
          </span>
        </div>
      </header>
      <div className="h-14 sm:h-[4.25rem]" aria-hidden />
      <main className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 sm:pt-14">
        <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
        <div className="mt-4 h-10 max-w-xl animate-pulse rounded-lg bg-slate-200" />
        <div className="mt-3 h-10 max-w-lg animate-pulse rounded-lg bg-slate-100" />
        <div className="mt-6 h-4 max-w-2xl animate-pulse rounded bg-slate-100" />
        <div className="mt-2 h-4 max-w-xl animate-pulse rounded bg-slate-100" />
      </main>
    </div>
  );
}

/**
 * Public marketing content (homepage, SEO, blog, legal).
 * Login/signup keep their own Light/Dark control and are excluded.
 */
export function isSaasMarketingContentPath(pathname: string): boolean {
  const path = (pathname || '/').split('?')[0] || '/';
  if (path === '/signup' || path === '/login' || path === '/reset-password') return false;
  return isSaasMarketingBootPath(path);
}

/** Paths where the branded splash must not flash over the marketing homepage. */
export function isSaasMarketingBootPath(pathname: string): boolean {
  const path = (pathname || '/').split('?')[0] || '/';
  if (path === '/' || path === '') return true;
  if (path === '/signup' || path === '/login' || path === '/reset-password') return true;
  if (path === '/industries') return true;
  if (path === '/blog' || path.startsWith('/blog/')) return true;
  if (path === '/privacy' || path === '/terms' || path === '/sales-agreement') return true;
  if (path === '/dpa' || path === '/subprocessors' || path === '/acceptable-use') return true;
  if (path === '/security-overview' || path === '/status') return true;
  // Industry SEO landings are marketing pages under the same shell.
  if (path.length > 1 && !path.startsWith('/dashboard') && !path.startsWith('/admin')) {
    const first = path.slice(1).split('/')[0] ?? '';
    const appPrefixes = new Set([
      'onboarding',
      'billing',
      'book',
      'site',
      'availability',
      'live-track',
      'setup',
      'enquiries',
      'ai-chat',
      'chat',
      'api',
    ]);
    if (!appPrefixes.has(first) && !first.includes('.')) {
      // Heuristic: most remaining public paths are industry SEO pages.
      return true;
    }
  }
  return false;
}
